import * as React from "react";
import classNames from "classnames";
import { $, merge } from "glamor";
import { format } from "lib/globalize-util";
import theme from "styles/theme";

interface IProps {
    sizeGroups?: any[];
    rows?: number;
    rowLabel?: any;
    children: any;
    sizeColumnWidth?: number;
    seperatorsAt?: number[];
    seperatorRenderer?: any;
    rowFooterRenderer?: any;
    showTotal?: boolean;
    getTotalInventory: (index: number) => number | string;
}

interface IState {
    headerRef: HTMLElement;
}

export class Sizes extends React.PureComponent<IProps, IState> {
    static defaultProps = {
        rows: 1,
    };

    private styles = merge(
        $(" .scrollableContent", {
            display: "inline-block",
        }),
        $(" .sizeHeader", {
            display: "flex",
        }),
        $(" .groupSizes", {
            display: "flex",
        }),
        $(" .sizesMain", {
            display: "flex",
        }),
        $(" .sizeRow", {
            display: "flex",
            width: "100%",
        }),
        $(" .sizeCell", {
            width: `${this.props.sizeColumnWidth || 80}px`,
            flexShrink: 0,
        }),
        $(" .sizeLabel.sizeCell", {
            width: "120px",
            flexShrink: 0,
            position: "sticky",
            left: 0,
        }),
        $(" .sizeHeader .sizeGroup.last", {
            flexGrow: 1,
        }),
        $(" .sizeRow .sizes, .sizeHeader .sizeGroup .sizeCell:last-child", {
            flexGrow: 1,
        }),
        $(" .total", {
            width: 60,
            maxWidth: 60,
            zIndex: 1000,
            overflow: "hidden",
            position: "sticky",
            right: 0,
            textAlign: "center",
            background: theme.palette.lighterGrey,
            borderLeft: `1px solid ${theme.palette.borderColor}`,
        }),
        $(" .sizeHeader .total", {
            width: 60,
            display: "table",
        }),
        $(" .sizeHeader .total .totalLabel", {
            verticalAlign: "bottom",
            textTransform: "uppercase",
            display: "table-cell",
        })
    );

    render() {
        const { sizeGroups, rowLabel, rows, children, seperatorsAt, seperatorRenderer, rowFooterRenderer } = this.props;
        const { showTotal, getTotalInventory } = this.props;

        if (!sizeGroups) {
            return null;
        }

        return (
            <div className="sizeTable" {...this.styles}>
                <div className="scrollableContent">
                    <header className="sizeHeader" ref={this.setRef}>
                        {rowLabel && <div className="sizeLabel sizeCell" />}
                        {sizeGroups.map((group, idx) => (
                            <div
                                key={group.name}
                                className={classNames("sizeGroup", { last: idx === sizeGroups.length - 1 })}
                            >
                                {group.name !== "" && <div className="sizeGroupName">{group.name}</div>}
                                <div className="groupSizes">
                                    {group.sizes.map((size, i) => (
                                        <div className="sizeCell" key={size._id}>
                                            {size._name}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                        {showTotal && (
                            <div
                                className="sizeCell total"
                                // required to get correct header height
                                style={{
                                    height:
                                        this.state && this.state.headerRef
                                            ? this.state.headerRef.getBoundingClientRect().height
                                            : "auto",
                                }}
                            >
                                <span className="totalLabel">{format("Total")}</span>
                            </div>
                        )}
                    </header>
                    {children && (
                        <div className="sizeBody">
                            {[...Array(rows).keys()].map((rowIndex) => {
                                if (seperatorRenderer && seperatorsAt.includes(rowIndex)) {
                                    return seperatorRenderer({ rowIndex });
                                }
                                return (
                                    <div key={rowIndex} className="sizeRow">
                                        {rowLabel && (
                                            <div key={rowIndex} className="sizeLabel sizeCell">
                                                {rowLabel({ rowIndex })}
                                            </div>
                                        )}
                                        <div className="sizes">
                                            <div className="sizesMain">
                                                {sizeGroups.map((group) => (
                                                    <React.Fragment key={group.name}>
                                                        {group.sizes.map((size, index) => (
                                                            <div
                                                                key={size._id}
                                                                data-test="arriving-size-cell"
                                                                className="sizeCell"
                                                            >
                                                                {children({ size, rowIndex, index })}
                                                            </div>
                                                        ))}
                                                    </React.Fragment>
                                                ))}
                                            </div>
                                            {rowFooterRenderer && (
                                                <div className="sizesFooter">{rowFooterRenderer({ rowIndex })}</div>
                                            )}
                                        </div>
                                        {showTotal && (
                                            <div
                                                key="total"
                                                data-test="arriving-total-inventory-amount"
                                                className="sizeCell total"
                                            >
                                                {getTotalInventory(rowIndex)}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    private setRef = (headerRef) => this.setState({ headerRef });
}
